// Ticket History Configuration
// This file defines how ticket changes are tracked and displayed

export const TICKET_HISTORY_CONFIG = {
  // Fields that should be tracked for changes
  trackedFields: [
    'ticketStatus',
    'priority',
    'developmentStatus',
    'assignTo',
    'asanaLink',
    'estimationTime',
    'deployedDate',
    'description',
    'comment',
    'type',
    'module',
    'category',
    'subCategory',
    'mappedId'
  ],

  // Field display names for history
  fieldDisplayNames: {
    ticketStatus: 'Ticket Status',
    priority: 'Priority',
    developmentStatus: 'Development Status',
    assignTo: 'Assigned To',
    asanaLink: 'Asana Link',
    estimationTime: 'Estimation Time',
    deployedDate: 'Deployed Date',
    description: 'Description',
    comment: 'Comment',
    type: 'Type',
    module: 'Module',
    category: 'Category',
    subCategory: 'SubCategory',
    mappedId: 'Mapped Items'
  },

  // Types of changes that can be tracked
  changeTypes: {
    CREATED: 'created',
    UPDATED: 'updated',
    MAPPED: 'mapped',
    STATUS_CHANGED: 'status_changed',
    ASSIGNED: 'assigned'
  },

  // Change type display names
  changeTypeDisplayNames: {
    created: 'Ticket Created',
    updated: 'Field Updated',
    mapped: 'Items Mapped',
    status_changed: 'Status Changed',
    assigned: 'Assignment Changed'
  },

  // Change type colors for UI
  changeTypeColors: {
    created: '#4caf50',
    updated: '#2196f3',
    mapped: '#9c27b0',
    status_changed: '#ff9800',
    assigned: '#607d8b'
  },

  // Change type icons
  changeTypeIcons: {
    created: 'add_circle',
    updated: 'edit',
    mapped: 'link',
    status_changed: 'swap_horiz',
    assigned: 'person_add'
  },

  // Fields that should trigger specific change types
  fieldChangeTypes: {
    ticketStatus: 'status_changed',
    developmentStatus: 'status_changed',
    assignTo: 'assigned',
    mappedId: 'mapped'
  },

  // History display settings
  display: {
    maxHistoryItems: 50, // Maximum number of history items to show
    showFieldChanges: true, // Show individual field changes
    showChangeDetails: true, // Show before/after values
    groupByDate: true, // Group history items by date
    showUserInfo: true, // Show who made the change
    showTimestamp: true, // Show when the change was made
    dateFormat: 'MMM dd, yyyy HH:mm', // Date format for display
    truncateLongValues: true, // Truncate long field values
    maxValueLength: 100 // Maximum length for field values
  },

  // History storage settings
  storage: {
    saveToDatabase: true, // Save history to database
    historyFieldName: 'history', // Field name in database
    includeOriginalValues: true, // Include original values in history
    includeNewValues: true, // Include new values in history
    includeChangeType: true, // Include change type in history
    includeUserInfo: true, // Include user info in history
    includeTimestamp: true // Include timestamp in history
  }
};

// Helper function to get change type for a field
export const getChangeTypeForField = (fieldName) => {
  return TICKET_HISTORY_CONFIG.fieldChangeTypes[fieldName] || 'updated';
};

// Helper function to get display name for a field
export const getFieldDisplayName = (fieldName) => {
  return TICKET_HISTORY_CONFIG.fieldDisplayNames[fieldName] || fieldName;
};

// Helper function to get change type display name
export const getChangeTypeDisplayName = (changeType) => {
  return TICKET_HISTORY_CONFIG.changeTypeDisplayNames[changeType] || 'Updated';
};

// Helper function to get change type color
export const getChangeTypeColor = (changeType) => {
  return TICKET_HISTORY_CONFIG.changeTypeColors[changeType] || '#2196f3';
};

// Helper function to check if a field should be tracked
export const isFieldTracked = (fieldName) => {
  return TICKET_HISTORY_CONFIG.trackedFields.includes(fieldName);
};

// Helper function to format field value for display
export const formatFieldValue = (value, fieldName) => {
  if (value === null || value === undefined || value === '') {
    return 'Not set';
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? `${value.length} item(s)` : 'No items';
  }

  if (typeof value === 'string' && value.length > TICKET_HISTORY_CONFIG.display.maxValueLength) {
    return value.substring(0, TICKET_HISTORY_CONFIG.display.maxValueLength) + '...';
  }

  return value.toString();
};

// Helper function to create history entry
export const createHistoryEntry = (changeType, fieldName, oldValue, newValue, user) => {
  const timestamp = new Date().toISOString();
  
  return {
    changeType,
    fieldName,
    fieldDisplayName: getFieldDisplayName(fieldName),
    oldValue: formatFieldValue(oldValue, fieldName),
    newValue: formatFieldValue(newValue, fieldName),
    user: {
      email: user.email || '',
      username: user.firstName || user.username || ''
    },
    timestamp,
    formattedTimestamp: new Date(timestamp).toLocaleString()
  };
};

// Helper function to compare objects and find changes
export const findChanges = (oldData, newData, user) => {
  const changes = [];
  
  TICKET_HISTORY_CONFIG.trackedFields.forEach(fieldName => {
    const oldValue = oldData[fieldName];
    const newValue = newData[fieldName];
    
    // Check if value has changed
    if (JSON.stringify(oldValue) !== JSON.stringify(newValue)) {
      const changeType = getChangeTypeForField(fieldName);
      const historyEntry = createHistoryEntry(changeType, fieldName, oldValue, newValue, user);
      changes.push(historyEntry);
    }
  });
  
  return changes;
};

// Helper function to merge history entries
export const mergeHistoryEntries = (existingHistory = [], newChanges = []) => {
  return [...existingHistory, ...newChanges].sort((a, b) => 
    new Date(b.timestamp) - new Date(a.timestamp)
  );
};

export default TICKET_HISTORY_CONFIG; 